import _ from 'lodash';
import { observable, action, computed } from 'mobx';
import { BaseViewStore } from 'core/stores';

class NavigationOptions {
    @observable accessor loading = false;
    @observable accessor title = null;
    // Holds a JSX element. Must be a shallow ref: a deep observable proxies the
    // React element, which breaks React 19's internal _debugTask
    // ('run' called with illegal receiver) when the element is committed.
    @observable.ref accessor subTitleComponent = null;
    @observable accessor hideColon;
    @observable accessor onPreview;
    @observable accessor onEdit;
    @observable accessor onCalendar;
    @observable accessor openModal;

    constructor() {
    }

    @action.bound
    setTitle(title) {
        this.title = title;
    }

    @action.bound
    setSubTitleComponent(subTitleComponent) {
        this.subTitleComponent = subTitleComponent;
    }

    @action.bound
    setHideColon(hideColon) {
        this.hideColon = hideColon;
    }

    @action.bound
    setOnPreview(onPreview) {
        this.onPreview = onPreview;
    }

    @action.bound
    setOnCalendar(onCalendar) {
        this.onCalendar = onCalendar;
    }

    @action.bound
    setOnEdit(onEdit) {
        this.onEdit = onEdit;
    }

    @action.bound
    setOpenModal(openModal) {
        this.openModal = openModal;
    }

    @action.bound
    suspend() {
        this.loading = true;
    }

    @action.bound
    resume() {
        this.loading = false;
    }
}

export default class MainViewStore extends BaseViewStore {
    @observable accessor mainMenuVisibile = false;
    @observable accessor profileMenuOpen = false;
    @observable accessor notificationMenuOpen = false;
    @observable accessor taskNotifications = [];
    @observable.ref accessor quickLinks = [];

    @observable accessor contentSidebarVisible = true;

    @observable accessor navigationOptions = null;

    constructor(rootStore) {
        super(rootStore);
    }

    @computed
    get breadcrumbs() {
        const activeMenuItem = this.rootStore.menuStore.activeMenuItem;
        if (!activeMenuItem) return [];

        let menuBreadcrumbs = _.map(activeMenuItem.path, menuItem => ({
            title: menuItem.breadcrumbTitle || menuItem.title,
            route: menuItem.route
        }));

        const { routes, routerState } = this.rootStore.routerStore;
        const activeRoute = _.find(routes, { name: routerState.routeName });

        let crumbs = [];
        // append only crumbs whose route isn't prefix of active menu item route
        const data = this.rootStore.routerStore.routeDataMap.get(activeRoute.name);
        if (data.crumbs && data.crumbs.length > 0) {
            crumbs = _.filter(data.crumbs,
                (c) => c.title && c.title !== '' && !_.startsWith(activeMenuItem.route, c.route)
            );
        }
        const templateRoutes = ["master.app.main.facility-order.facility-order-template.edit",
         "master.app.main.facility-order.pick-ticket.list", 
         "master.app.main.facility-order.overview.edit-email",
         "master.app.main.facility-order.documentation-request.edit-email",
         "master.app.main.facility-order.facility-inventory-calculation-items.list",
         "master.app.main.facility-order.facility-order-new-flow.inventory-edit",
         "master.app.main.facility-order.facility-order-new-flow.create-patient",
         "master.app.main.facility-order.facility-order-new-flow.us-intake-create-patient",
         "master.app.main.facility-order.facility-order-new-flow.clinical-preview-create-patient",
         "master.app.main.facility-order.facility-order-new-flow.overview",
         "master.app.main.facility-order.documentation-request-new-flow.edit-email",
         "master.app.main.facility-order.inventory-request.new-flow-edit-email",
         "master.app.main.facility-order.ongoing-missing-inventory.new-flow-edit-email",
         "master.app.main.niko-facility-order.niko-inventory-request.new-flow-edit-email",
         "master.app.main.niko-facility-order.niko-ongoing-missing-inventory.new-flow-edit-email",
         "master.app.main.niko-facility-order.niko-overview.new-flow-edit-email",
         "master.app.main.niko-facility-order.niko-documentation-request"
        ];

        if(crumbs.some(c => templateRoutes.some(r => r === c.route))){
            crumbs = crumbs.map(obj => { 
                if (obj.route === "master.app.main.niko-facility-order.inventory-preview" || obj.route === "master.app.main.niko-facility-order.us-intake-preview" || obj.route === "master.app.main.facility-order.facility-order-template.list" || obj.route === "master.app.main.facility-order.facility-order-new-flow.us-intake-preview" || obj.route === "master.app.main.facility-order.facility-order-new-flow.preview" || obj.route === "master.app.main.facility-order.facility-order-new-flow.clinical-preview" || obj.route === "master.app.main.facility-order.facility-order-new-flow.inventory-preview" || "master.app.main.niko-facility-order.clinical-review-preview"){
                    return  { ...obj, params: { id: routerState.params.facilityOrderId || routerState.params.id }};
                }
                else if (obj.route === "master.app.main.facility-order.facility-inventory-calculation-items.list"){
                    return  { ...obj, params: { facilityOrderId: routerState.params.facilityOrderId }};
                }
                return obj;
            });
        }

        return [
            ...menuBreadcrumbs,
            ...crumbs
        ];
    }

    @action.bound
    toggleContentSidebarVisibility() {
        this.contentSidebarVisible = !this.contentSidebarVisible;
    }

    @action.bound
    setNavigationOptions(navigationOptions) {
        const opts = _.isFunction(navigationOptions)
                ? navigationOptions(this.rootStore)
                : navigationOptions;

        let options = null;
        if (opts && opts.title) {
            options = new NavigationOptions();
            if (_.isFunction(opts.title)) {
                const result = opts.title(this.rootStore);
                if (result instanceof Promise) {
                    options.suspend();
                    result.then((res) => {
                        options.setTitle(res);
                        options.resume();
                    })
                } else {
                    options.setTitle(result);
                }
            } else {
                options.setTitle(opts.title);
                options.setSubTitleComponent(opts.subTitleComponent);
            }
            options.setOnPreview(opts.onPreview);
            options.setOnCalendar(opts.onCalendar);
            options.setOnEdit(opts.onEdit);
            options.setHideColon(opts.hideColon);
            options.setOpenModal(opts.openModal);
        }
        
        this.navigationOptions = options;
    }

    @action.bound
    setSubTitleComponent(subTitleComponent) {
        if(this.navigationOptions){
            this.navigationOptions.setSubTitleComponent(subTitleComponent);
        }
    }
    
    @action.bound
    setMainMenuVisibile(visible) {
        this.mainMenuVisibile = visible;
    }

    @action.bound
    toggleMainMenuVisibility() {
        this.mainMenuVisibile = !this.mainMenuVisibile;
    }

    @action.bound
    toggleProfileMenu() {
        this.profileMenuOpen = !this.profileMenuOpen;
    }

    @action.bound
    setProfileMenu(visible) {
        this.profileMenuOpen = visible;
    }

    @action.bound
    toggleNotificationMenu() {
        this.notificationMenuOpen = !this.notificationMenuOpen;
    }

    @action.bound
    setNotificationMenu(visible) {
        this.notificationMenuOpen = visible;
    }

    @action.bound
    async logout() {
        const baasicApp = this.rootStore.application.baasic;
        const {
            authStore
        } = this.rootStore;
        const token = baasicApp.getAccessToken();

        await baasicApp.membershipModule.login.logout(token.token, token.type);
        authStore.resetSignInRedirect();
        this.rootStore.navigateLogin();
    }
}
